import React from 'react';
import { AlertTriangle, Shield, TrendingUp, DollarSign, BarChart2 } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения оценки рисков валютной ипотеки
 */
const RiskAssessment = ({ riskAnalysis, formData, currencyRates }) => {
  if (!riskAnalysis) return null;

  // Определение цветовой схемы в зависимости от уровня риска
  const getRiskColorScheme = () => {
    switch (riskAnalysis.currencyRiskLevel) {
      case 'low':
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          text: 'text-green-800 dark:text-green-300',
          textMuted: 'text-green-600 dark:text-green-400',
          darkBg: 'bg-green-100 dark:bg-green-900/30',
          iconColor: 'text-green-500',
        };
      case 'medium':
        return {
          bg: 'bg-amber-50 dark:bg-amber-900/20',
          border: 'border-amber-200 dark:border-amber-800',
          text: 'text-amber-800 dark:text-amber-300',
          textMuted: 'text-amber-600 dark:text-amber-400',
          darkBg: 'bg-amber-100 dark:bg-amber-900/30',
          iconColor: 'text-amber-500',
        };
      case 'high':
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          text: 'text-red-800 dark:text-red-300',
          textMuted: 'text-red-600 dark:text-red-400',
          darkBg: 'bg-red-100 dark:bg-red-900/30',
          iconColor: 'text-red-500',
        };
      default:
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          text: 'text-blue-800 dark:text-blue-300',
          textMuted: 'text-blue-600 dark:text-blue-400',
          darkBg: 'bg-blue-100 dark:bg-blue-900/30',
          iconColor: 'text-blue-500',
        };
    }
  };

  const colors = getRiskColorScheme();
  
  // Получение текстового описания уровня риска
  const getRiskLevelText = () => {
    switch (riskAnalysis.currencyRiskLevel) {
      case 'low':
        return 'Низкий уровень риска';
      case 'medium':
        return 'Средний уровень риска';
      case 'high':
        return 'Высокий уровень риска';
      default:
        return 'Неопределенный уровень риска';
    }
  };
  
  // Получение иконки для уровня риска
  const getRiskIcon = () => {
    switch (riskAnalysis.currencyRiskLevel) {
      case 'low':
        return <Shield className={`w-6 h-6 ${colors.iconColor}`} />;
      case 'medium':
        return <AlertTriangle className={`w-6 h-6 ${colors.iconColor}`} />;
      case 'high':
        return <AlertTriangle className={`w-6 h-6 ${colors.iconColor}`} />;
      default:
        return <AlertTriangle className={`w-6 h-6 ${colors.iconColor}`} />;
    }
  };

  // Получение рекомендаций
  const recommendations = riskAnalysis.recommendations || [];

  return (
    <div className={`mb-8 ${colors.bg} p-5 rounded-xl ${colors.border} shadow-sm`}>
      <div className="flex items-center gap-3 mb-4">
        {getRiskIcon()}
        <h3 className={`text-xl font-semibold ${colors.text}`}>
          {getRiskLevelText()} для валютной ипотеки в {formData.currency}
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
        {/* Базовый платеж vs Платеж при неблагоприятном курсе */}
        <div className={`p-4 rounded-lg ${colors.darkBg} ${colors.border}`}>
          <h4 className={`text-sm font-medium ${colors.text} mb-2 flex items-center gap-1.5`}>
            <DollarSign className="w-4 h-4" />
            Текущий ежемесячный платеж в рублях
          </h4>
          <div className="flex items-baseline gap-2">
            <span className={`text-xl font-bold ${colors.text}`}>
              {formatNumber(riskAnalysis.baseMonthlyPaymentRUB)} ₽
            </span>
            <span className={`text-sm ${colors.textMuted}`}>
              ({formatNumber(riskAnalysis.baseMonthlyPayment)} {formData.currency})
            </span>
          </div>
        </div>

        <div className={`p-4 rounded-lg ${colors.darkBg} ${colors.border}`}>
          <h4 className={`text-sm font-medium ${colors.text} mb-2 flex items-center gap-1.5`}>
            <AlertTriangle className="w-4 h-4" />
            При неблагоприятном курсе (+50%)
          </h4>
          <div className="flex items-baseline gap-2">
            <span className={`text-xl font-bold ${colors.text}`}>
              {formatNumber(riskAnalysis.worstCasePaymentRUB)} ₽
            </span>
            <span className={`text-sm ${colors.textMuted}`}>
              (+{formatNumber(riskAnalysis.worstCasePaymentRUB - riskAnalysis.baseMonthlyPaymentRUB)} ₽)
            </span>
          </div>
        </div>

        {/* Нагрузка на бюджет */}
        <div className={`p-4 rounded-lg ${colors.darkBg} ${colors.border}`}>
          <h4 className={`text-sm font-medium ${colors.text} mb-2 flex items-center gap-1.5`}>
            <BarChart2 className="w-4 h-4" />
            Текущая нагрузка на бюджет (DTI)
          </h4>
          <div className="flex items-baseline gap-2">
            <span className={`text-xl font-bold ${colors.text}`}>
              {formatNumber(riskAnalysis.currentDTI, 1)}%
            </span>
            <span className={`text-sm ${colors.textMuted}`}>
              от вашего месячного дохода
            </span>
          </div>
        </div>

        <div className={`p-4 rounded-lg ${colors.darkBg} ${colors.border}`}>
          <h4 className={`text-sm font-medium ${colors.text} mb-2 flex items-center gap-1.5`}>
            <TrendingUp className="w-4 h-4" />
            Нагрузка при неблагоприятном курсе
          </h4>
          <div className="flex items-baseline gap-2">
            <span className={`text-xl font-bold ${colors.text}`}>
              {formatNumber(riskAnalysis.worstCaseDTI, 1)}%
            </span>
            <span className={`text-sm ${colors.textMuted}`}>
              (+{formatNumber(riskAnalysis.worstCaseDTI - riskAnalysis.currentDTI, 1)}%)
            </span>
          </div>
        </div>
      </div>

      {/* Прогноз курса */}
      <div className={`p-4 rounded-lg ${colors.darkBg} ${colors.border} mb-4`}>
        <h4 className={`text-sm font-medium ${colors.text} mb-2`}>
          Прогноз динамики курса {formData.currency}/RUB по сценарию "{riskAnalysis.scenarioName}"
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Начальный курс</span>
            <div className={`text-sm font-medium ${colors.text}`}>
              1 {formData.currency} = {formatNumber(riskAnalysis.exchangeRateForecast.startRate, 2)} ₽
            </div>
          </div>
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Прогнозируемый конечный курс</span>
            <div className={`text-sm font-medium ${colors.text}`}>
              1 {formData.currency} = {formatNumber(riskAnalysis.exchangeRateForecast.endRate, 2)} ₽
            </div>
          </div>
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Годовое изменение (прогноз)</span>
            <div className={`text-sm font-medium ${colors.text}`}>
              {riskAnalysis.exchangeRateForecast.trend > 0 ? '+' : ''}{formatNumber(riskAnalysis.exchangeRateForecast.trend, 1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Рекомендации */}
      <div className="space-y-2">
        <h4 className={`text-base font-medium ${colors.text}`}>
          Рекомендации по снижению валютных рисков:
        </h4>
        <ul className="space-y-1.5">
          {recommendations.map((recommendation, index) => (
            <li key={index} className="flex items-start gap-2">
              <div className="mt-1">
                <Shield className={`w-3.5 h-3.5 ${colors.iconColor}`} />
              </div>
              <span className={`text-sm ${colors.textMuted}`}>{recommendation}</span>
            </li>
          ))}
        </ul>
      </div>
      
      {/* Дополнительная информация */}
      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        * DTI (Debt-to-Income Ratio) - отношение ежемесячного платежа к ежемесячному доходу, измеряется в процентах.
        <br />
        * Оптимальный уровень DTI не должен превышать 40% от вашего дохода.
      </div>
    </div>
  );
};

export default RiskAssessment;